import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { getUsers } from '../../../store/slices/usersSlice';
import { getUser } from '../../../store/slices/userSlice';
import UserDetailsDialog from './UserDetailsDialog';
import AddUserDrawer from './AddUserDialog';
import {
  canAccessUsersPage,
  primaryRoleLabel,
  formatRoleLabel,
  primaryRole as getPrimaryRole,
} from '../../../utils/permissions';
import useIsMobile from '../../../hooks/useIsMobile';
import { scaleColumnsForMobile } from '../../../utils/mobileDataGrid';

const RolesCell = ({ roles }) => {
  const list = Array.isArray(roles) ? roles : [];
  if (list.length === 0) return <Typography variant="body2" sx={{ color: 'text.secondary' }}>—</Typography>;
  const primary = formatRoleLabel(getPrimaryRole({ roles: list }));
  const extra = list.length - 1;
  const tooltipText = list.map(formatRoleLabel).join(', ');
  return (
    <Tooltip title={tooltipText} arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{primary}</Typography>
        {extra > 0 && (
          <Chip
            size="small"
            label={`+${extra}`}
            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

const columns = [
  {field: 'first_name', headerName: 'First Name', width: 150 },
  {field: 'last_name', headerName: 'Last Name', width: 150 },
  {field: 'email', headerName: 'Email', width: 250 },
  {field: 'username', headerName: 'Username', width: 150 },
  {
    field: 'roles',
    headerName: 'Role',
    width: 180,
    sortable: false,
    valueGetter: (value, row) => primaryRoleLabel({ roles: row.roles || [] }),
    renderCell: (params) => <RolesCell roles={params.row.roles} />,
  },
  {
    field: 'is_active',
    headerName: 'Status',
    width: 110,
    valueGetter: (value, row) => (row.is_active === false ? 'Inactive' : 'Active'),
    renderCell: (params) => (
      <Chip
        size="small"
        label={params.row.is_active === false ? 'Inactive' : 'Active'}
        color={params.row.is_active === false ? 'default' : 'success'}
        variant={params.row.is_active === false ? 'outlined' : 'filled'}
        sx={{ fontWeight: 600, fontSize: '0.75rem' }}
      />
    ),
  },
  {field: 'phone', headerName: 'Phone', width: 150},
  {field: 'created_at', headerName: 'Created At', width: 130,valueFormatter: value => value ? value.slice(0, 10) : '',},
  {field: 'updated_at', headerName: 'Updated At', width: 130,valueFormatter: value => value ? value.slice(0, 10) : '',},
  {field: 'last_login_at', headerName: 'Last Login At', width: 130,valueFormatter: value => value ? value.slice(0, 10) : '',},
];

const paginationModel = { page: 0, pageSize: 30 };

export default function Users() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useIsMobile();
  const users = useSelector(getUsers);
  const user = useSelector(getUser);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [addUserDrawerOpen, setAddUserDrawerOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  const displayColumns = React.useMemo(
    () => (isMobile ? scaleColumnsForMobile(columns) : columns),
    [isMobile],
  );

  // Flat user list (each user has roles[]). Technicians live in their own tab/slice bucket.
  const allUsers = users.list || [];

  // Filter users based on search query
  const filteredUsers = React.useMemo(() => {
    if (!searchQuery) return allUsers;
    const query = searchQuery.toLowerCase();
    return allUsers.filter(u =>
      u.first_name?.toLowerCase().includes(query) ||
      u.last_name?.toLowerCase().includes(query) ||
      u.email?.toLowerCase().includes(query) ||
      u.username?.toLowerCase().includes(query) ||
      (u.roles || []).some((r) => r?.toLowerCase().includes(query)) ||
      u.phone?.toLowerCase().includes(query)
    );
  }, [allUsers, searchQuery]);

  // Display user details drawer
  const handleRowSelectionModelChange = (newRowSelectionModel) => {
    if (newRowSelectionModel.ids.size > 0) {
      const userId = newRowSelectionModel.ids.values().next().value;
      const selectedUser = allUsers.find(u => u.id === userId);
      if (selectedUser) setSelectedUser(selectedUser);
    }
  };

  // Error message
  if (users.error) {
    return (
      <Box sx={{
        color: 'text.primary',
        bgcolor: 'background.paper',
        padding: '24px',
        borderRadius: '8px',
        margin: '40px auto',
        maxWidth: '400px',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '1.2rem',
        boxShadow: isDarkMode ? '0 2px 8px rgba(211,47,47,0.3)' : '0 2px 8px rgba(211,47,47,0.15)'
      }}>
        <span style={{ fontSize: '2rem', display: 'block', marginBottom: '12px' }}>⚠️</span>
        {users.error}
      </Box>
    );
  }

  // Only owner can view this page
  if (!canAccessUsersPage(user)) {
    return (
      <Box sx={{
        color: 'text.primary',
        bgcolor: 'background.paper',
        padding: '24px',
        borderRadius: '8px',
        margin: '40px auto',
        maxWidth: '400px',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '1.2rem',
        boxShadow: isDarkMode ? '0 2px 8px rgba(211,47,47,0.3)' : '0 2px 8px rgba(211,47,47,0.15)'
      }}>
        <span style={{ fontSize: '2rem', display: 'block', marginBottom: '12px' }}>🔒</span>
        You are not authorized to view this page
      </Box>
    );
  }

  return (
    <>
      <Paper 
        elevation={0}
        sx={{ 
          height: '100%', 
          width: '100%', 
          padding: { xs: 1, md: 3 },
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header with title and Add User button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, md: 3 }, gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
          <Typography 
            variant="h4" 
            component="h1"
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '1.25rem', md: '2rem' },
              color: 'text.primary',
            }}
          >
            Users
          </Typography>
          {canAccessUsersPage(user) && (
            <Button
              variant="contained"
              onClick={() => setAddUserDrawerOpen(true)}
              sx={{
                backgroundColor: isDarkMode ? '#fff' : '#000',
                color: isDarkMode ? '#000' : '#fff',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                px: 3,
                py: 1.25,
                borderRadius: 1.5,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                  boxShadow: 'none',
                }
              }}
            >
              + Add User
            </Button>
          )}
        </Box>

        {/* Search bar */}
        <Box sx={{ mb: { xs: 2, md: 3 }, flexShrink: 0 }}>
          <TextField
            fullWidth
            placeholder="Search users by name, email, username, role, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                backgroundColor: 'background.paper',
                fontSize: '0.875rem',
                height: '42px',
                '& input::placeholder': {
                  color: 'text.secondary',
                  opacity: 1,
                },
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#e0e0e0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#e0e0e0',
                  borderWidth: '1px',
                },
              }
            }}
          />
        </Box>

        {/* Data Grid */}
        <Box sx={{ 
          backgroundColor: 'background.paper', 
          borderRadius: 2,
          border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
          overflow: 'hidden',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}>
          <DataGrid
            rows={filteredUsers}
            loading={users.loading}
            columns={displayColumns}
            density={isMobile ? 'compact' : 'standard'}
            initialState={{ pagination: { paginationModel } }}
            pageSizeOptions={[5, 10, 20, 30, 50, 100]}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            sx={{ 
              border: 0,
              height: '100%',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
              },
              '& .MuiDataGrid-columnHeaderTitle': { 
                fontWeight: 600,
                fontSize: '0.8125rem',
                color: 'text.secondary',
              },
              '& .MuiDataGrid-cell': { 
                fontSize: '0.875rem',
                color: 'text.primary',
                borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #f5f5f5',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                },
              },
              '& .MuiDataGrid-footerContainer': { 
                borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
              },
            }}
          />
        </Box>
      </Paper>

      {/* User details drawer */}
      {UserDetailsDialog({ selectedUser, setSelectedUser, user })}
      {/* Add user drawer */}
      {canAccessUsersPage(user) && AddUserDrawer({ setAddUserDrawerOpen, addUserDrawerOpen, user })}
    </>
  );
}


